import React, { useMemo } from 'react';
import { Star, Circle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ParetoSolution {
  config_hash: string;
  objective_values: Record<string, number>;
  iteration: number;
  is_best: boolean;
}

interface IterationData {
  iteration: number;
  objective_values: Record<string, number>;
  is_on_pareto: boolean;
}

interface ParetoChartProps {
  pareto: ParetoSolution[];
  iterations?: IterationData[];
  xObjective: string;
  yObjective: string;
  xDirection?: 'maximize' | 'minimize';
  yDirection?: 'maximize' | 'minimize';
  width?: number;
  height?: number;
  className?: string;
}

export function ParetoChart({
  pareto,
  iterations = [],
  xObjective,
  yObjective,
  xDirection = 'maximize',
  yDirection = 'maximize',
  width = 400,
  height = 300,
  className,
}: ParetoChartProps) {
  // Calculate chart bounds and scale
  const { bounds, scale, paretoPoints, dominatedPoints, bestPoint } = useMemo(() => {
    // Collect all points
    const allPoints = [
      ...pareto.map(p => ({
        x: p.objective_values[xObjective] ?? 0,
        y: p.objective_values[yObjective] ?? 0,
        iteration: p.iteration,
        hash: p.config_hash,
        isBest: p.is_best,
        isPareto: true,
      })),
      ...iterations.filter(it => !it.is_on_pareto).map(it => ({
        x: it.objective_values[xObjective] ?? 0,
        y: it.objective_values[yObjective] ?? 0,
        iteration: it.iteration,
        hash: '',
        isBest: false,
        isPareto: false,
      })),
    ];

    if (allPoints.length === 0) {
      return {
        bounds: { minX: 0, maxX: 1, minY: 0, maxY: 1 },
        scale: { x: 1, y: 1 },
        paretoPoints: [],
        dominatedPoints: [],
        bestPoint: null,
      };
    }

    // Calculate bounds with padding
    const xValues = allPoints.map(p => p.x);
    const yValues = allPoints.map(p => p.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    const xPadding = (maxX - minX) * 0.1 || 0.1;
    const yPadding = (maxY - minY) * 0.1 || 0.1;

    const bounds = {
      minX: minX - xPadding,
      maxX: maxX + xPadding,
      minY: minY - yPadding,
      maxY: maxY + yPadding,
    };

    // Chart area (leaving space for axes)
    const chartWidth = width - 60;
    const chartHeight = height - 50;

    const scale = {
      x: chartWidth / (bounds.maxX - bounds.minX),
      y: chartHeight / (bounds.maxY - bounds.minY),
    };

    const paretoPoints = allPoints.filter(p => p.isPareto);
    const dominatedPoints = allPoints.filter(p => !p.isPareto);
    const bestPoint = paretoPoints.find(p => p.isBest);

    return { bounds, scale, paretoPoints, dominatedPoints, bestPoint };
  }, [pareto, iterations, xObjective, yObjective, width, height]);

  // Transform data point to SVG coordinates
  const toSvg = (x: number, y: number) => {
    // Flip Y axis for SVG (origin at top-left)
    const svgX = 50 + (x - bounds.minX) * scale.x;
    const svgY = height - 40 - (y - bounds.minY) * scale.y;
    return { x: svgX, y: svgY };
  };

  // Generate Pareto frontier line
  const paretoLine = useMemo(() => {
    if (paretoPoints.length < 2) return '';

    // Sort by x value
    const sorted = [...paretoPoints].sort((a, b) => a.x - b.x);

    const points = sorted.map(p => {
      const svg = toSvg(p.x, p.y);
      return `${svg.x},${svg.y}`;
    });

    return `M ${points.join(' L ')}`;
  }, [paretoPoints, bounds, scale]);

  // Format value for display
  const formatValue = (value: number) => {
    if (Math.abs(value) < 0.01) return value.toExponential(2);
    if (Math.abs(value) >= 1000) return value.toFixed(0);
    return value.toFixed(3);
  };

  // Generate axis ticks
  const xTicks = useMemo(() => {
    const tickCount = 5;
    const range = bounds.maxX - bounds.minX;
    const step = range / tickCount;
    return Array.from({ length: tickCount + 1 }, (_, i) => bounds.minX + step * i);
  }, [bounds]);

  const yTicks = useMemo(() => {
    const tickCount = 4;
    const range = bounds.maxY - bounds.minY;
    const step = range / tickCount;
    return Array.from({ length: tickCount + 1 }, (_, i) => bounds.minY + step * i);
  }, [bounds]);

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>Pareto Frontier</span>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-xs">
              <Circle className="h-2 w-2 mr-1 fill-muted-foreground" />
              Dominated
            </Badge>
            <Badge variant="outline" className="text-xs text-blue-600">
              <Circle className="h-2 w-2 mr-1 fill-blue-600" />
              Pareto
            </Badge>
            <Badge variant="outline" className="text-xs text-amber-600">
              <Star className="h-2 w-2 mr-1 fill-amber-600" />
              Best
            </Badge>
          </div>
        </CardTitle>
        <CardDescription>
          {xObjective} ({xDirection}) vs {yObjective} ({yDirection})
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <svg
            width={width}
            height={height}
            className="font-mono text-xs"
            viewBox={`0 0 ${width} ${height}`}
          >
            {/* Grid lines */}
            <g className="text-muted stroke-muted">
              {xTicks.map((tick, i) => {
                const svg = toSvg(tick, bounds.minY);
                return (
                  <line
                    key={`x-grid-${i}`}
                    x1={svg.x}
                    y1={height - 40}
                    x2={svg.x}
                    y2={10}
                    strokeDasharray="2,2"
                    strokeOpacity={0.3}
                  />
                );
              })}
              {yTicks.map((tick, i) => {
                const svg = toSvg(bounds.minX, tick);
                return (
                  <line
                    key={`y-grid-${i}`}
                    x1={50}
                    y1={svg.y}
                    x2={width - 10}
                    y2={svg.y}
                    strokeDasharray="2,2"
                    strokeOpacity={0.3}
                  />
                );
              })}
            </g>

            {/* X Axis */}
            <line
              x1={50}
              y1={height - 40}
              x2={width - 10}
              y2={height - 40}
              className="stroke-foreground"
            />
            {xTicks.map((tick, i) => {
              const svg = toSvg(tick, bounds.minY);
              return (
                <g key={`x-tick-${i}`}>
                  <line
                    x1={svg.x}
                    y1={height - 40}
                    x2={svg.x}
                    y2={height - 35}
                    className="stroke-foreground"
                  />
                  <text
                    x={svg.x}
                    y={height - 25}
                    textAnchor="middle"
                    className="fill-muted-foreground"
                    fontSize={10}
                  >
                    {formatValue(tick)}
                  </text>
                </g>
              );
            })}
            <text
              x={width / 2}
              y={height - 5}
              textAnchor="middle"
              className="fill-foreground"
              fontSize={11}
            >
              {xObjective} {xDirection === 'minimize' ? '(minimize)' : '(maximize)'}
            </text>

            {/* Y Axis */}
            <line
              x1={50}
              y1={10}
              x2={50}
              y2={height - 40}
              className="stroke-foreground"
            />
            {yTicks.map((tick, i) => {
              const svg = toSvg(bounds.minX, tick);
              return (
                <g key={`y-tick-${i}`}>
                  <line
                    x1={45}
                    y1={svg.y}
                    x2={50}
                    y2={svg.y}
                    className="stroke-foreground"
                  />
                  <text
                    x={42}
                    y={svg.y + 3}
                    textAnchor="end"
                    className="fill-muted-foreground"
                    fontSize={10}
                  >
                    {formatValue(tick)}
                  </text>
                </g>
              );
            })}
            <text
              x={15}
              y={height / 2}
              textAnchor="middle"
              className="fill-foreground"
              fontSize={11}
              transform={`rotate(-90, 15, ${height / 2})`}
            >
              {yObjective}
            </text>

            {/* Pareto frontier line */}
            {paretoLine && (
              <path
                d={paretoLine}
                fill="none"
                className="stroke-blue-500"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Dominated points */}
            {dominatedPoints.map((point, i) => {
              const svg = toSvg(point.x, point.y);
              return (
                <Tooltip key={`dom-${i}`}>
                  <TooltipTrigger asChild>
                    <circle
                      cx={svg.x}
                      cy={svg.y}
                      r={4}
                      className="fill-muted-foreground/50 stroke-muted-foreground cursor-pointer hover:r-6"
                      strokeWidth={1}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <p className="font-medium">Iteration {point.iteration}</p>
                      <p>{xObjective}: {formatValue(point.x)}</p>
                      <p>{yObjective}: {formatValue(point.y)}</p>
                      <p className="text-muted-foreground">Dominated</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}

            {/* Pareto points */}
            {paretoPoints.filter(p => !p.isBest).map((point, i) => {
              const svg = toSvg(point.x, point.y);
              return (
                <Tooltip key={`pareto-${i}`}>
                  <TooltipTrigger asChild>
                    <circle
                      cx={svg.x}
                      cy={svg.y}
                      r={5}
                      className="fill-blue-500 stroke-blue-700 cursor-pointer hover:r-7"
                      strokeWidth={2}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <p className="font-medium">Iteration {point.iteration}</p>
                      <p>{xObjective}: {formatValue(point.x)}</p>
                      <p>{yObjective}: {formatValue(point.y)}</p>
                      <p className="text-blue-600">On Pareto Frontier</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}

            {/* Best point */}
            {bestPoint && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <g>
                    <circle
                      cx={toSvg(bestPoint.x, bestPoint.y).x}
                      cy={toSvg(bestPoint.x, bestPoint.y).y}
                      r={8}
                      className="fill-amber-400 stroke-amber-600 cursor-pointer"
                      strokeWidth={2}
                    />
                    <text
                      x={toSvg(bestPoint.x, bestPoint.y).x}
                      y={toSvg(bestPoint.x, bestPoint.y).y + 4}
                      textAnchor="middle"
                      className="fill-amber-900 pointer-events-none"
                      fontSize={10}
                    >
                      ★
                    </text>
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs">
                    <p className="font-medium text-amber-600">Best Solution</p>
                    <p>Iteration {bestPoint.iteration}</p>
                    <p>{xObjective}: {formatValue(bestPoint.x)}</p>
                    <p>{yObjective}: {formatValue(bestPoint.y)}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </svg>
        </TooltipProvider>

        {/* Legend / Stats */}
        <div className="flex justify-between text-xs text-muted-foreground mt-2 pt-2 border-t">
          <span>{paretoPoints.length} solutions on frontier</span>
          <span>{dominatedPoints.length} dominated</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Iteration history table component
interface IterationHistoryProps {
  iterations: IterationData[];
  objectives: string[];
  className?: string;
}

export function IterationHistory({ iterations, objectives, className }: IterationHistoryProps) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Iteration History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-[200px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b">
                <th className="text-left py-1 px-2">#</th>
                {objectives.map(obj => (
                  <th key={obj} className="text-right py-1 px-2">{obj}</th>
                ))}
                <th className="text-center py-1 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {iterations.map((it, i) => (
                <tr key={i} className={cn(
                  "border-b",
                  it.is_on_pareto && "bg-blue-50 dark:bg-blue-950/20"
                )}>
                  <td className="py-1 px-2">{it.iteration}</td>
                  {objectives.map(obj => (
                    <td key={obj} className="text-right py-1 px-2 font-mono">
                      {(it.objective_values[obj] ?? 0).toFixed(4)}
                    </td>
                  ))}
                  <td className="text-center py-1 px-2">
                    {it.is_on_pareto ? (
                      <Badge variant="default" className="text-xs">Pareto</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Dominated</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default ParetoChart;
